import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import type { DesignerCanvas } from './types/api';
import { useCurrentTenant, useMe } from './lib/graphql/hooks';
import { FeatureErrorBoundary } from './components/ErrorBoundary';
import { TestTube, Home } from 'lucide-react';
import { useAuth, TokenStorage } from './contexts';

// Lazy load route components for code splitting
const VisualDesigner = lazy(() => import('./components/VisualDesigner').then(module => ({ default: module.VisualDesigner })));
const GraphQLIntegrationTest = lazy(() => import('./components/GraphQLIntegrationTest').then(module => ({ default: module.GraphQLIntegrationTest })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function AppWithRouting() {
  const [tenantId, setTenantId] = useState<string>('');
  const { isAuthenticated, isInitialized } = useAuth();

  // =====================================================
  // GRAPHQL QUERIES
  // =====================================================

  const {
    data: tenantData,
    loading: tenantLoading,
    error: tenantError,
  } = useCurrentTenant({
    skip: !isAuthenticated,
  });

  const {
    data: _userData,
    loading: userLoading,
    error: userError,
  } = useMe({
    skip: !isAuthenticated,
  });

  // =====================================================
  // INITIALIZATION
  // =====================================================

  useEffect(() => {
    if (tenantData?.currentTenant) {
      setTenantId(tenantData.currentTenant.id);
      localStorage.setItem('tenant_id', tenantData.currentTenant.id);
    } else if (!isAuthenticated && isInitialized) {
      // For demo purposes, use a default tenant ID when not authenticated
      setTenantId('demo-tenant-001');
      localStorage.setItem('tenant_id', 'demo-tenant-001');
    }
  }, [tenantData, isAuthenticated, isInitialized]);

  // =====================================================
  // CANVAS MANAGEMENT
  // =====================================================

  const handleSave = async (canvas: DesignerCanvas) => {
    try {
      // Save canvas state to backend
      console.log('Saving canvas:', canvas);

      // In the future, implement a GraphQL mutation for saving canvas state
      // await saveCanvasMutation({ variables: { tenantId, canvas } });

      alert('Canvas saved successfully!');
    } catch (error) {
      console.error('Failed to save canvas:', error);
      alert('Failed to save canvas. Please try again.');
    }
  };

  const handleExport = async (canvas: DesignerCanvas) => {
    try {
      console.log('Exporting canvas:', canvas);

      const exportData = {
        features: canvas.features,
        connections: canvas.connections,
        metadata: {
          exportedAt: new Date(),
          tenantId,
          version: '1.0',
        },
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feature-composition-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert('Canvas exported successfully!');
    } catch (error) {
      console.error('Failed to export canvas:', error);
      alert('Failed to export canvas. Please try again.');
    }
  };

  // =====================================================
  // LOADING & ERROR STATES
  // =====================================================

  const loading = tenantLoading || userLoading;
  const error = tenantError || userError;

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4" role="img" aria-label="Error icon">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Retry connection"
            >
              Retry
            </button>
            <button
              onClick={() => {
                TokenStorage.clear();
                setTenantId('demo-tenant-001');
                window.location.reload();
              }}
              className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              aria-label="Continue in demo mode"
            >
              Continue as Demo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <BrowserRouter>
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Navigation */}
        <nav className="bg-white border-b border-gray-200 px-6 py-3" role="navigation" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h1 className="text-xl font-bold text-gray-900">LiquidStack</h1>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Go to Designer"
                >
                  <Home className="h-4 w-4" aria-hidden="true" />
                  <span className="text-sm font-medium">Designer</span>
                </Link>
                <Link
                  to="/test"
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Go to API Tests"
                >
                  <TestTube className="h-4 w-4" aria-hidden="true" />
                  <span className="text-sm font-medium">API Tests</span>
                </Link>
              </div>
            </div>
            <div className="text-sm text-gray-600" aria-live="polite">
              Tenant: <span className="font-medium">{tenantId}</span>
            </div>
          </div>
        </nav>

        {/* Routes */}
        <main className="flex-1 overflow-hidden" role="main">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <FeatureErrorBoundary featureName="Visual Designer">
                    <VisualDesigner
                      tenantId={tenantId}
                      onSave={handleSave}
                      onExport={handleExport}
                    />
                  </FeatureErrorBoundary>
                }
              />
              <Route
                path="/test"
                element={
                  <FeatureErrorBoundary featureName="GraphQL Integration Test">
                    <GraphQLIntegrationTest />
                  </FeatureErrorBoundary>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default AppWithRouting;
